<template>
    <div :class="['flex h-auto w-full grow flex-row flex-nowrap justify-evenly pl-0 xl:block xl:w-auto xl:flex-col xl:justify-normal xl:pl-[10%]', wizardStore.getIsFinished ? 'hidden' : '']">
        <div v-for="(step, index) in steps" :key="step.id">
            <button :class="['mx-0 my-3 grid grid-flow-row items-center gap-2 px-2 xl:grid-flow-col', { 'hover:opacity-80': canNavigateToStep(index) }]" @click="switchStep(index)" :disabled="!canNavigateToStep(index)">
                <template v-if="step.icon">
                    <FontAwesomeLayers v-if="step.isValid && !step.hasError" :class="['w-full', isMobile ? 'fa-2x' : 'fa-3x', isStepActive(index) ? 'text-sidebar-icon-active' : 'text-sidebar-icon-inactive']">
                        <FontAwesomeIcon icon="fa-sharp fa-thin fa-circle" />
                        <FontAwesomeIcon icon="fa-sharp fa-solid fa-check" transform="shrink-9" />
                    </FontAwesomeLayers>
                    <FontAwesomeLayers v-else :class="['w-full', isMobile ? 'fa-2x' : 'fa-3x', isStepActive(index) ? 'text-sidebar-icon-active' : 'text-sidebar-icon-inactive']">
                        <FontAwesomeIcon icon="fa-sharp fa-thin fa-circle" />
                        <FontAwesomeIcon :icon="step.icon" transform="shrink-9" />
                    </FontAwesomeLayers>
                </template>
                <template v-else>
                    <FontAwesomeLayers v-if="step.isValid && !step.hasError" :class="['w-full', isMobile ? 'fa-2x' : 'fa-3x', isStepActive(index) ? 'text-sidebar-icon-active' : 'text-sidebar-icon-inactive']">
                        <FontAwesomeIcon icon="fa-sharp fa-thin fa-circle" />
                        <FontAwesomeIcon icon="fa-sharp fa-solid fa-check" transform="shrink-9" />
                    </FontAwesomeLayers>
                    <div v-else :class="['rounded-full border p-2', isMobile ? 'size-8' : 'size-12', isStepActive(index) ? 'border-sidebar-icon-active' : 'border-sidebar-icon-inactive']">
                        <div v-html="step.iconSvg" :class="['fill-current', isStepActive(index) ? 'text-sidebar-icon-active' : 'text-sidebar-icon-inactive']" />
                    </div>
                </template>
                <div class="ml-0 mt-1 flex flex-col flex-nowrap items-center justify-center xl:items-start xl:px-2">
                    <span :class="['text-left text-xs', step.hasError ? 'text-sidebar-text-error-c' : isStepActive(index) ? 'text-sidebar-step-title-c' : 'text-sidebar-step-title-i-c']">{{ step.translateName ? $t(step.name) : step.name }}</span>
                    <span :class="['hidden text-left font-medium xl:block', isStepActive(index) ? 'text-sidebar-step-subt-c' : 'text-sidebar-step-subt-i-c']">
                        {{ step.value ?? $t(step.nullPlaceholder) }}
                    </span>
                </div>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { FontAwesomeLayers } from '@fortawesome/vue-fontawesome';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { DateTime } from 'luxon';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import router from '@/router';
import { useUserStore } from '@/stores/user';
import { useWizardStore } from '@/stores/wizard';

const breakpoints = useBreakpoints(breakpointsTailwind);
const wizardStore = useWizardStore();
const userStore = useUserStore();
const isMobile = breakpoints.smaller('xl');
const { d, n } = useI18n();

const steps = computed(() => [
    {
        id: 1,
        name: 'clinicService-title', // key from i18n
        translateName: true,
        nullPlaceholder: 'select-action', // key from i18n
        value: wizardStore.steps[0]?.clinic?.name,
        icon: 'fa-sharp fa-solid fa-location-dot',
        href: wizardStore.steps[0]?.href,
        isValid: wizardStore.steps[0]?.isValid,
        hasError: wizardStore.steps[0]?.hasError,
    },
    {
        id: 2,
        name: 'serviceSelection-title', // key from i18n
        translateName: true,
        nullPlaceholder: 'select-action', // key from i18n
        value:
            wizardStore.steps[1]?.service?.price && wizardStore.steps[1]?.service?.name
                ? wizardStore.steps[1]?.service?.name +
                  // @ts-expect-error - i18n bug: ignoring incorrect typing for currency format options
                  ` (${n(wizardStore.steps[1]?.service?.price, 'currency', { key: 'currency', currency: userStore.getCurrencyCode })})`
                : wizardStore.steps[1]?.service?.name ?? null,
        iconSvg:
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 720 720"><path d="M518.96,621c-26.11,0-52.22-9.94-72.1-29.82l-102.6-102.6c-39.71-39.71-39.6-104.44,.25-144.3,10.84-10.84,16.78-25.29,16.74-40.68-.04-15.42-6.07-29.91-16.97-40.81l-102.56-102.56c-22.43-22.43-58.93-22.43-81.37,0-22.43,22.43-22.43,58.93,0,81.37l102.47,102.7c8.66,8.68,8.65,22.73-.02,31.4l-102.57,102.57c-22.43,22.43-22.43,58.93,0,81.37,22.43,22.43,58.93,22.43,81.37,0l46.21-46.21c8.68-8.68,22.74-8.68,31.42,0,8.68,8.68,8.68,22.74,0,31.42l-46.21,46.21c-39.76,39.76-104.45,39.76-144.21,0-39.76-39.76-39.76-104.45,0-144.21l86.87-86.87-86.78-86.97c-39.74-39.74-39.74-104.43,.02-144.19,39.76-39.76,104.45-39.76,144.21,0l102.56,102.56c19.26,19.26,29.9,44.86,29.98,72.11,.08,27.32-10.49,52.97-29.75,72.23-22.53,22.53-22.64,59.07-.25,81.46l102.6,102.6c22.43,22.43,58.93,22.43,81.37,0,22.5-22.5,22.55-59.05,.12-81.48l-102.42-102.42c-8.67-8.67-8.68-22.72-.02-31.4l102.42-102.71c22.46-22.46,22.46-58.96,.02-81.39-22.43-22.43-58.91-22.46-81.3-.07l-.04,.04-46.36,46.13c-8.7,8.65-22.77,8.62-31.42-.08-8.65-8.7-8.62-22.77,.08-31.42l46.34-46.11c19.24-19.23,44.8-29.81,71.98-29.81h.09c27.21,.02,52.79,10.64,72.05,29.9,39.76,39.76,39.76,104.45,0,144.21l-86.73,86.98,86.73,86.73c39.76,39.76,39.71,104.5-.12,144.32-19.88,19.88-45.99,29.82-72.1,29.82Z"/></svg>',
        href: wizardStore.steps[1]?.href,
        isValid: wizardStore.steps[1]?.isValid,
        hasError: wizardStore.steps[1]?.hasError,
    },
    {
        id: 3,
        name: isMobile.value ? 'staffSelection-mobile-title' : wizardStore.steps[2]?.staff ? wizardStore.steps[2]?.staff.name : 'staffSelection-title', // key from i18n
        translateName: isMobile.value || (!isMobile.value && !wizardStore.steps[2]?.staff),
        nullPlaceholder: 'select-action', // key from i18n
        value:
            wizardStore.steps[2]?.selectedDay || wizardStore.steps[2]?.selectedTime
                ? `${wizardStore.steps[2]?.selectedDay ? d(wizardStore.steps[2]?.selectedDay, 'short') : ''} ${wizardStore.steps[2]?.selectedTime ? getTimeFormat(wizardStore.steps[2].selectedTime, wizardStore.steps[1]?.service?.duration) : ''}`
                : null,
        icon: 'fa-sharp fa-solid fa-clock',
        href: wizardStore.steps[2]?.href,
        isValid: wizardStore.steps[2]?.isValid,
        hasError: wizardStore.steps[2]?.hasError,
    },
    {
        id: 4,
        name: isMobile.value ? 'clientForm-mobile-title' : 'clientForm-title', // key from i18n
        translateName: true,
        nullPlaceholder: 'fill-action', // key from i18n
        value: null,
        icon: 'fa-sharp fa-solid fa-pen',
        href: wizardStore.steps[3]?.href,
        isValid: wizardStore.steps[3]?.isValid,
        hasError: wizardStore.steps[3]?.hasError,
    },
]);

const canNavigateToStep = (stepIndex: number) => {
    if (wizardStore.canGoBack) {
        // Navigation is allowed if previous steps are valid
        return steps.value.slice(0, stepIndex).every((step) => step.isValid);
    } else {
        return stepIndex > 1; // Allow navigation to staff and client form steps
    }
};

const isStepActive = (stepIndex: number) => {
    return stepIndex === wizardStore.currentStep;
};

const switchStep = (index: number) => {
    router.push({ name: steps.value[index]?.href });
};

const getTimeFormat = (inputDatetime, duration = 30) => {
    const parsedDatetime = DateTime.fromISO(inputDatetime);
    const updatedDatetime = parsedDatetime.plus({ minutes: duration });

    const startTime = parsedDatetime.toFormat('HH:mm');
    const endTime = updatedDatetime.toFormat('HH:mm');
    return `${startTime} - ${endTime}`;
};
</script>
